/**
 * /for-pm — PM-focused funnel landing. Single audience, single message,
 * single CTA. Designed to be the destination for PM-targeted Google /
 * LinkedIn / X ads, so it's tighter than the main homepage — hero +
 * 3 PM scenarios + a single "start free" CTA. Visitor either clicks the
 * CTA into the app or bounces; no second story to read.
 */
import { useTranslation } from 'react-i18next';
import { MarketingShell } from './MarketingShell';

export function ForPm() {
  const { t } = useTranslation();

  return (
    <MarketingShell
      activeRoute="/for-pm"
      currentLocale={typeof window !== 'undefined' && window.location.pathname.startsWith('/zh/') ? 'zh' : 'en'}
      hreflangPath="/for-pm"
      documentTitle={t('forPm.pageTitle')}
      documentDescription={t('forPm.pageDescription')}
    >
      {/* ── Hero ── */}
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 88 }}>
        <div style={{ maxWidth: 880 }}>
          <span className="label label-amber">{t('forPm.heroLabel')}</span>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(44px, 5.6vw, 76px)',
              lineHeight: 1,
              margin: '12px 0 24px',
            }}
          >
            {t('forPm.heroTitle')}
            <br />
            <em>{t('forPm.heroTitleEm')}</em>
          </h1>
          <p className="lede" style={{ maxWidth: 720 }}>{t('forPm.heroLede')}</p>
          <div className="actions" style={{ marginTop: 28 }}>
            <a className="btn-primary" href="/">{t('forPm.ctaButton')}</a>
            <a className="btn-ghost" href="/pricing">{t('marketing.navPricing')}</a>
          </div>
          <p className="hero-roi" style={{ marginTop: 24, maxWidth: 640 }}>
            {t('homepage.heroRoiLine')}
          </p>
        </div>
      </section>

      {/* ── 3 PM scenarios (numbered, like homepage problem section) ── */}
      <section className="problem">
        <div className="head">
          <h2 className="serif">
            {t('homepage.problemHeading')} <em>{t('homepage.problemHeadingEm')}</em>
          </h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">{t('forPm.scenario1Title')}</h3>
            <p>{t('forPm.scenario1Body')}</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">{t('forPm.scenario2Title')}</h3>
            <p>{t('forPm.scenario2Body')}</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">{t('forPm.scenario3Title')}</h3>
            <p>{t('forPm.scenario3Body')}</p>
          </div>
        </div>
      </section>

      {/* ── Trust strip mini-pricing teaser ── */}
      <section className="pricing" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div className="head">
          <h2 className="serif">
            {t('forPm.ctaTitle')} <em>{t('forPm.ctaTitleEm')}</em>
          </h2>
        </div>
        <div className="trust-strip">
          <span>{t('homepage.pricingTrust1')}</span>
          <span>{t('homepage.pricingTrust2')}</span>
          <span>{t('homepage.pricingTrust3')}</span>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '40px 24px',
            border: '1px solid var(--ink-0)',
            borderTop: 0,
            background: 'var(--paper-1)',
          }}
        >
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            {t('forPm.ctaBody')}
          </p>
          <a className="btn-primary" href="/login">{t('forPm.ctaButton')}</a>
        </div>
      </section>
    </MarketingShell>
  );
}
